import { useEffect, useState } from "react";
import { UserX } from "lucide-react";

interface MemberNotFoundModalProps {
  open: boolean;
  onClose: () => void;
  dni: string;
}

const AUTO_CLOSE_SECONDS = 3;

export const MemberNotFoundModal = ({ open, onClose, dni }: MemberNotFoundModalProps) => {
  const [timeLeft, setTimeLeft] = useState(AUTO_CLOSE_SECONDS);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Handle open/close animations
  useEffect(() => {
    if (open) {
      setIsExpanded(true);
      setIsClosing(false);
      const contentTimer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(contentTimer);
    }
  }, [open]);

  // Auto-close timer
  useEffect(() => {
    if (!open || !isExpanded) {
      setTimeLeft(AUTO_CLOSE_SECONDS);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleClose();
          return AUTO_CLOSE_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, isExpanded]);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setShowContent(false);
    
    setTimeout(() => {
      setIsExpanded(false);
    }, 200);
    
    setTimeout(() => {
      onClose();
      setIsClosing(false);
      setTimeLeft(AUTO_CLOSE_SECONDS);
    }, 700);
  };

  if (!open && !isClosing) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden"
      onClick={handleClose}
    >
      {/* Expanding red circle background */}
      <div 
        className={`
          absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          rounded-full bg-red-600
          transition-all duration-500 ease-out
        `}
        style={{
          width: isExpanded ? '250vmax' : '0px',
          height: isExpanded ? '250vmax' : '0px',
        }}
      />

      {/* Progress bar */}
      <div 
        className={`
          absolute top-0 left-0 right-0 h-2 bg-white/20 z-10
          transition-opacity duration-300
          ${showContent ? 'opacity-100' : 'opacity-0'}
        `}
      >
        <div 
          className="h-full bg-white transition-all duration-1000 ease-linear"
          style={{ width: `${(timeLeft / AUTO_CLOSE_SECONDS) * 100}%` }}
        />
      </div>

      {/* Content */}
      <div 
        className={`
          absolute inset-0 flex flex-col items-center justify-center
          transition-opacity duration-300
          ${showContent ? 'opacity-100' : 'opacity-0'}
        `}
        style={{ pointerEvents: showContent ? 'auto' : 'none' }}
      >
        <div className="flex flex-col items-center text-center space-y-8 p-8">
          {/* Large icon */}
          <div className="h-48 w-48 rounded-full bg-white/10 flex items-center justify-center">
            <UserX className="h-32 w-32 text-white" />
          </div>

          {/* Large text */}
          <h2 className="text-6xl font-bold tracking-tight text-white uppercase">
            Socio no encontrado
          </h2>

          {/* DNI */}
          <p className="text-3xl text-white/70 font-mono">{dni}</p>

          <p className="text-lg text-white/70 mt-8">
            Toca en cualquier lugar para cerrar • {timeLeft}s
          </p>
        </div>
      </div>
    </div>
  );
};
